import { ArrowDownOutlined, ArrowUpOutlined, PlusOutlined } from '@ant-design/icons';
import { Collapse } from 'antd';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import CycleDotGraph from './CycleDotGraph';
import { Button } from 'components/ui';
import { colors } from 'design';

const useStyles = createUseStyles({
  stubOverlayContainer: {
    position: 'relative',
  },
  stubOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(2px)',

    '& p': {
      margin: 0,
    },
  },
  stubTextContainer: {
    maxWidth: '80%',
    background: `radial-gradient(closest-side, rgba(255,255,255,1), rgba(255,255,255,0))`,
    // background: 'rgba(255,255,255,0.7)',
    // boxShadow: '0 0 16px 0 rgb(255, 255, 255, 1)',
    textAlign: 'center',
    padding: 24,
    borderRadius: 8,
  },
  summaryHeader: {
    display: 'flex',
    gap: 32,
    marginTop: 8,
    marginBottom: 24,
  },
  noDataText: {
    color: colors.text.tertiary,
    textAlign: 'center',
  },
  unavailableStat: {
    color: colors.text.disabled,
  },
  plusMinus: {
    fontSize: 17,
    color: colors.text.disabled,
    fontWeight: 500,
  },
  summaryBlock: {
    color: colors.text.disabled,
    fontSize: 14,
    '& strong': {
      display: 'flex',
      color: colors.text.primary,
      fontSize: 28,
      fontWeight: 700,
      lineHeight: 1,
      alignItems: 'center',
      gap: 4,
    },
  },
  periodHeader: {
    fontSize: 14,
    color: colors.text.disabled,
    fontWeight: 500,
    margin: 0,
  },
  mainCallToActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 24,
  },
  collapse: {
    '& .ant-collapse-content-box': {
      padding: '0',
    },
    '& .ant-collapse-body': {
      padding: '0',
    },

    '& .ant-collapse-header': {
      textAlign: 'center',
      '& svg': {
        marginRight: 8,
      },
    },
  },
  bloodDrop: {
    height: 12,
    width: 12,
  },
});

const PastCyclesLength: React.FC<{ onFillCycles: () => void }> = ({ onFillCycles }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [collapseOpen, setCollapseOpen] = useState(false);

  const { cycleStats, cycleHistory } = useSymptomTrackingData();

  const noData = cycleHistory.length === 0;
  const onlyOneCycle = cycleHistory.length === 1;

  return (
    <>
      {cycleHistory.length > 0 && (
        <div className={styles.stubOverlayContainer}>
          <div className={styles.summaryHeader}>
            <div className={styles.summaryBlock}>
              {t('symptomTracking.cycles.averageDuration')}
              <strong>
                <span>
                  {onlyOneCycle ? <span className={styles.unavailableStat}>-</span> : Math.round(cycleStats.average)}{' '}
                  <span style={{ fontSize: 16 }}>{t('symptomTracking.nextPeriod.days')}</span>
                </span>
              </strong>
            </div>
            <div className={styles.summaryBlock}>
              {t('symptomTracking.cycles.standardDeviation')}
              <strong>
                {!onlyOneCycle && <span className={styles.plusMinus}>±</span>}
                <span>
                  {onlyOneCycle ? (
                    <span className={styles.unavailableStat}>-</span>
                  ) : (
                    cycleStats.roundedStandardDeviation
                  )}{' '}
                  <span style={{ fontSize: 16 }}>{t('symptomTracking.nextPeriod.days')}</span>
                </span>
              </strong>
            </div>
          </div>
          <div>
            <h4 className={styles.periodHeader}>{t('symptomTracking.cycles.currentCycle')}</h4>
            <CycleDotGraph cycle={cycleHistory[cycleHistory.length - 1]} cycleStats={cycleStats} />
            {!onlyOneCycle && <h4 className={styles.periodHeader}>{t('symptomTracking.cycles.previousCycles')}</h4>}
            {/* Render previous cycles with collapse if more than 3 */}
            {(() => {
              const previousCycles = cycleHistory.slice(0, cycleHistory.length - 1).reverse();
              if (previousCycles.length <= 3) {
                return previousCycles.map(period => (
                  <CycleDotGraph key={period.start.format('DD/MM/YYYY')} cycle={period} cycleStats={cycleStats} />
                ));
              }
              const firstThree = previousCycles.slice(0, 3);
              const nextSix = previousCycles.slice(3, 9); // up to 6 more
              return (
                <>
                  {firstThree.map(period => (
                    <CycleDotGraph key={period.start.format('DD/MM/YYYY')} cycle={period} cycleStats={cycleStats} />
                  ))}
                  <Collapse
                    ghost
                    bordered={false}
                    activeKey={collapseOpen ? ['more'] : []}
                    onChange={keys => setCollapseOpen(!!keys.length)}
                    className={styles.collapse}
                    items={[
                      {
                        key: 'more',
                        label: collapseOpen ? (
                          <>
                            <ArrowUpOutlined />
                            {t('symptomTracking.cycles.showLess')}
                          </>
                        ) : (
                          <>
                            <ArrowDownOutlined />
                            {t('symptomTracking.cycles.showMore')}
                          </>
                        ),
                        showArrow: false,
                        children: (
                          <>
                            {nextSix.map(period => (
                              <CycleDotGraph
                                key={period.start.format('DD/MM/YYYY')}
                                cycle={period}
                                cycleStats={cycleStats}
                              />
                            ))}
                          </>
                        ),
                      },
                    ]}
                  />
                </>
              );
            })()}
          </div>
        </div>
      )}
      {noData && (
        <>
          <p className={styles.noDataText}>{t('symptomTracking.cycles.fillAtLeastOneDay')}</p>
          <Button variant="link" icon={<PlusOutlined />} block onClick={onFillCycles}>
            {t('symptomTracking.cycles.fillCycles')}
          </Button>
        </>
      )}
      {onlyOneCycle && (
        <Button variant="primary" icon={<PlusOutlined />} block size="lg" onClick={onFillCycles}>
          {t('symptomTracking.cycles.pastCycles')}
        </Button>
      )}
    </>
  );
};

export default PastCyclesLength;
